import type { SegmentDTO } from '@next-space/fe-api-idl';
import { BlockType, CardColorSource, CollectionViewType } from '@next-space/fe-api-idl';
import { isUndefined, omitBy, sample } from 'lodash-es';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { i18n } from '@flowus/common/i18n';
import { COLORS } from 'src/bitable/const';
import type { RecordCreatedFrom } from 'src/bitable/table-view/types';
import { message } from 'src/common/components/message';
import { request } from 'src/common/request';
import { useTransaction } from 'src/hooks/use-transaction';
import {
  LIST_AFTER_COLLECTION_VIEW_PAGESORT,
  LIST_BEFORE_COLLECTION_VIEW_PAGESORT,
} from 'src/redux/actions';
import { addBlock } from 'src/redux/managers/block/add';
import { updateBlock } from 'src/redux/managers/block/update';
import * as CollectionViewManager from 'src/redux/managers/collection-view';
import { dispatch, getState } from 'src/redux/store';
import type { CollectionWhere, NextBlock } from 'src/redux/types';
import { $appUiStateCache, setAppUiState } from 'src/services/app';
import { sequence } from 'src/utils/async-utils';
import { useOpenInRight } from '../page/use-open-in-right';
import { useCurrentUser } from '../user';
import { getViewFormat } from './get-view-format';
import { isOpenSubitem } from './use-open-subitem';
import { updateParentChildRalationId } from 'src/redux/managers/block/move';
import { v4 as uuidV4 } from 'uuid';

/**
 * bitable
 * 以排序的形式, 插入新记录
 */

export const useInsertRecord = () => {
  const transaction = useTransaction();
  const openInRight = useOpenInRight();
  const { setting = {} } = useCurrentUser();
  const history = useHistory();

  return useCallback(
    async (params: {
      viewId: string;
      where?: CollectionWhere;
      propertyValues?: Record<string, SegmentDTO[] | undefined>;
      templateId?: string;
      isOpenInRight?: boolean;
      from?: RecordCreatedFrom;
    }) => {
      const { viewId, where, propertyValues, templateId, isOpenInRight, from } = params;
      const defers: (() => Promise<void>)[] = [];
      const recordId = transaction(
        () => {
          const recordId = insertRecord(viewId, where, propertyValues);
          if (recordId != null && templateId != null) {
            fillRecordWithTemplate(recordId, templateId, defers);
          }
          // 如果是subitem，需要把关联属性也更新一下（后台会更新但本地数据没刷新，只能本地数据再更新一把）

          const collectionSearch = $appUiStateCache.$collectionSearch;
          setAppUiState({
            $collectionSearch: {
              ...collectionSearch,
              [viewId]: undefined,
            },
          });

          if (recordId) {
            if (isOpenInRight) {
              if (setting.useRightPageByNew) {
                openInRight(recordId);
              } else {
                history.push(`/${recordId}`);
              }
            } else {
              setAppUiState({ $newCreatedRecord: { id: recordId, from, viewId } });
            }
          }

          return recordId;
        },
        { noThrottle: true }
      );
      void sequence(async () => {
        await Promise.all(defers.map((it) => it()));
      });
      return recordId;
    },
    [transaction, setting.useRightPageByNew, openInRight, history]
  );
};

/**
 * 插入 记录
 * @param viewId CollectionView ID
 * @param after 插入锚点
 */
export const insertRecord = (
  viewId: string,
  where?: CollectionWhere,
  propertyValues?: Record<string, SegmentDTO[] | undefined>
) => {
  const viewInfo = getViewFormat(viewId);
  if (!viewInfo) return;

  const { view, collection } = viewInfo;
  const newRecordId = uuidV4();
  const blockInfo: Partial<NextBlock> = { type: BlockType.PAGE, uuid: newRecordId };
  blockInfo.data = {};

  if (propertyValues !== undefined) {
    const { title, ...properties } = propertyValues;
    blockInfo.data.collectionProperties = omitBy(properties, isUndefined) as Record<
      string,
      SegmentDTO[]
    >;
    blockInfo.data.segments = title;
    // 添加子记录的时候，如果开启了subitem需要更新一下parentItem的值（后台会更新，但本地不能刷新一遍数据，只能本地数据再加一次）
    const openSubitem = isOpenSubitem(collection.uuid);
    if (openSubitem) {
      const subitem = collection.data.format?.openSubItem;
      if (subitem?.childPropertyId && subitem.parentPropertyId) {
        const parentIds = properties[subitem.parentPropertyId]?.map((v) => v.uuid);
        // 如果加的是父的，就需要更新父的childPropertyId
        parentIds?.forEach((v) => {
          if (v) {
            updateParentChildRalationId(collection.uuid, v, newRecordId);
          }
        });
      }
    }
  }

  let isCustomColor = false;
  if (view.type === CollectionViewType.CALENDAR) {
    isCustomColor = view.format.calendarColorSetting?.type === CardColorSource.CUSTOM;
  } else if (view.type === CollectionViewType.TIMELINE) {
    isCustomColor = view.format.timelineColorSetting?.type === CardColorSource.CUSTOM;
  }
  if (isCustomColor) {
    const pool = COLORS.map((item) => item.key);
    const colorKey = sample(pool) ?? COLORS[0].key;
    blockInfo.data.collectionCardColor = colorKey;
  }

  addBlock(blockInfo, {
    parentId: collection.uuid,
    // 多维表 collection subNodes 的排序永远都是插入排序
    // 具体的页面顺序通过 CollectionView.pageSort
    last: true,
  });

  const { newPageSort, needUpdate } = checkPageSort(view.pageSort, collection.subNodes);
  if (needUpdate) {
    CollectionViewManager.update(viewId, {
      pageSort: newPageSort,
    });
  }
  if (where?.first || where?.before) {
    dispatch(
      LIST_BEFORE_COLLECTION_VIEW_PAGESORT({ uuid: newRecordId, viewId, before: where.before })
    );
  } else {
    dispatch(
      LIST_AFTER_COLLECTION_VIEW_PAGESORT({ uuid: newRecordId, viewId, after: where?.after })
    );
  }

  collection.views?.forEach((uuid) => {
    if (uuid === viewId) return;
    dispatch(LIST_AFTER_COLLECTION_VIEW_PAGESORT({ uuid: newRecordId, viewId: uuid }));
  });

  return newRecordId;
};

export const checkPageSort = (pageSort: string[], subNodes: string[]) => {
  const pageSortSet = new Set(pageSort);
  const subNodesSet = new Set(subNodes);

  let needUpdate = false;
  // 少了的补上
  for (const blockId of subNodes) {
    if (!pageSortSet.has(blockId)) {
      needUpdate = true;
      pageSortSet.add(blockId);
    }
  }

  // 多了的拿掉
  for (const blockId of pageSort) {
    if (!subNodesSet.has(blockId)) {
      needUpdate = true;
      pageSortSet.delete(blockId);
    }
  }

  return {
    needUpdate,
    newPageSort: [...pageSortSet],
  };
};

export const fillRecordWithTemplate = (
  recordId: string,
  templateId: string,
  outDefers: (() => Promise<void>)[] = []
) => {
  const record = getState().blocks[recordId];
  if (record == null) {
    // eslint-disable-next-line no-console
    console.warn(i18n.t('hooks.block.insert_record.record_not_found', { recordId }));
    return;
  }
  const template = getState().blocks[templateId];
  if (template == null) {
    // eslint-disable-next-line no-console
    console.warn(i18n.t('hooks.block.insert_record.template_not_found', { templateId }));
    return;
  }

  const patch: Partial<Omit<NextBlock, 'uuid'>> = {};
  const props = { ...record.data.collectionProperties };
  for (const [prop, value] of Object.entries(template.data.collectionProperties ?? {})) {
    const isTemplateEmptyProperty = value.length === 0 && value[0]?.text === '';
    if (!props[prop] && !isTemplateEmptyProperty) {
      props[prop] = value;
    }
  }
  patch.data = {
    collectionProperties: props,
  };

  patch.data.pageFixedWidth = template.data.pageFixedWidth;
  patch.data.directoryMenu = template.data.directoryMenu;

  if (record.data.cover == null) {
    patch.data.cover = template.data.cover;
  }
  if (record.data.coverPos == null) {
    patch.data.coverPos = template.data.coverPos;
  }
  if (record.data.icon == null) {
    patch.data.icon = template.data.icon;
  }
  if (record.data.iconUrl == null) {
    patch.data.iconUrl = template.data.iconUrl;
  }
  // 新建的记录需要使用模板的设置
  if (template.data.format?.pageLayout) {
    if (!patch.data.format) {
      patch.data.format = {};
    }
    patch.data.format.pageLayout = template.data.format?.pageLayout;
  }
  if (template.data.format?.fontFormat) {
    if (!patch.data.format) {
      patch.data.format = {};
    }
    patch.data.format.fontFormat = template.data.format?.fontFormat;
  }

  updateBlock(recordId, patch);
  if (record.subNodes.length === 0 && template.subNodes.length === 0) {
    addBlock({ type: BlockType.TEXTAREA }, { parentId: recordId, last: true });
  }

  if (template.subNodes.length > 0) {
    outDefers.push(async () => {
      const res = await request.editor.copySubNodes.raw({
        list: [
          {
            uuid: template.uuid,
            targetId: recordId,
          },
        ],
      });
      // 如果复制接口失败也不用管
      if (res.code !== 200) {
        message.error(res.msg);
      }
    });
  }
};
